import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import '../styles/UserManagement.css';
import {
  Business,
  PersonAdd,
  Group,
  Shield,
  Add,
  Edit,
  Delete,
  VpnKey,
  Refresh
} from '@mui/icons-material';

const FUNCTIONS_BASE_URL = `https://europe-west2-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net`;
const USERS_PAGE_SIZE = 50;

function UserManagement() {
  // The user list. status is one of:
  //   'loading'       first page on its way; nothing to show yet
  //   'ready'         every page has been fetched
  //   'loading-more'  the fetched prefix is visible; more pages are on their way
  //   'error'         the last request failed; error says why
  // mfaLookupError is set when the server could not check MFA for a page, in
  // which case those users carry mfa_enrolled: null and show as unknown.
  const [directory, setDirectory] = useState({
    status: 'loading',
    users: [],
    nextPageToken: null,
    error: null,
    mfaLookupError: null
  });
  // Bumped by every user-list request. A response whose generation is no
  // longer current is dropped: a "Load more" that was in flight when the
  // first page was refreshed (after adding or deleting a user) must not
  // append its stale page to the fresh list.
  const usersLoadGeneration = useRef(0);
  // 'loading' | 'ready' | 'error'
  const [organisationsState, setOrganisationsState] = useState({ status: 'loading', organisations: [], error: null });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [accountConfirmation, setAccountConfirmation] = useState(null);
  // Message from the last failed per-user action (delete, remove MFA).
  const [actionError, setActionError] = useState('');
  const [sessionRetries, setSessionRetries] = useState({});
  // A destructive action awaiting confirmation: { kind: 'delete' | 'remove-mfa', user }.
  // The dialog names the user; the request goes out with their immutable user_id.
  const [pendingAction, setPendingAction] = useState(null);
  const [runningAction, setRunningAction] = useState(false);
  const { getAuthToken, checkRole, userClaims } = useAuth();
  const isGod = checkRole('god');
  const isAdmin = checkRole('admin');

  const emptyNewUser = () => ({
    email: '',
    password: '',
    name: '',
    // An admin can only add users to their own organisation.
    organisation: isAdmin && !isGod && userClaims?.organisation ? userClaims.organisation : '',
    role: 'user'
  });
  const [newUser, setNewUser] = useState(emptyNewUser);

  // Calls one of the Site's Cloud Functions with the signed-in user's token.
  // Resolves with the parsed JSON body on success; rejects with an Error
  // carrying the backend's own message (or the HTTP status) on any failure,
  // so callers never mistake a refusal for an empty result.
  async function callSiteFunction(functionName, { method = 'GET', body = null, query = null } = {}) {
    const token = await getAuthToken();
    const url = new URL(`${FUNCTIONS_BASE_URL}/${functionName}`);
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== null && value !== undefined) url.searchParams.set(key, value);
      });
    }
    let response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: body ? JSON.stringify(body) : undefined
      });
    } catch (error) {
      error.outcomeUnknown = true;
      throw error;
    }
    let data = null;
    try {
      data = await response.json();
    } catch (parseError) {
      data = null;
    }
    if (!response.ok || !data || data.success !== true) {
      const reason = (data && data.error) || `HTTP ${response.status}`;
      const error = new Error(reason);
      error.outcomeUnknown = !data;
      error.details = data;   // the backend's whole payload, for callers that need more than the message
      throw error;
    }
    return data;
  }

  // Follow every continuation, including pages containing only stale entries.
  // Each published prefix retains the next cursor so a failed request resumes
  // there. A newer refresh or unmount stops this chain before its next request.
  async function loadUsers({ append = false } = {}) {
    let pageToken = append ? directory.nextPageToken : null;
    const users = new Map((append ? directory.users : []).map((user) => [user.user_id, user]));
    let mfaLookupError = append ? directory.mfaLookupError : null;
    const seenTokens = new Set();
    const generation = ++usersLoadGeneration.current;
    setDirectory({
      status: append ? 'loading-more' : 'loading',
      users: [...users.values()], nextPageToken: pageToken,
      error: null, mfaLookupError
    });
    try {
      do {
        if (seenTokens.has(pageToken)) {
          throw new Error('The server repeated a page of users. Please try again.');
        }
        seenTokens.add(pageToken);
        const data = await callSiteFunction('get_users', {
          query: { page_size: USERS_PAGE_SIZE, page_token: pageToken }
        });
        if (generation !== usersLoadGeneration.current) return;
        data.users.forEach((user) => users.set(user.user_id, user));
        pageToken = data.next_page_token || null;
        mfaLookupError = data.mfa_lookup_error || mfaLookupError;
        setDirectory({
          status: pageToken ? 'loading-more' : 'ready',
          users: [...users.values()], nextPageToken: pageToken,
          error: null, mfaLookupError
        });
      } while (pageToken);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (generation !== usersLoadGeneration.current) return;
      setDirectory((current) => ({
        ...current,
        status: 'error',
        error: `Could not finish loading users: ${error.message}`
      }));
    }
  }

  async function loadOrganisations() {
    setOrganisationsState({ status: 'loading', organisations: [], error: null });
    try {
      const data = await callSiteFunction('get_organisations');
      setOrganisationsState({ status: 'ready', organisations: data.organisations || [], error: null });
    } catch (error) {
      console.error('Error fetching organisations:', error);
      setOrganisationsState({ status: 'error', organisations: [], error: `Could not load organisations: ${error.message}` });
    }
  }

  useEffect(() => {
    // Both loaders read the signed-in user through the context; this page is
    // only mounted for a resolved admin or god session.
    loadUsers();
    loadOrganisations();
    return () => { usersLoadGeneration.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If user is an admin, set their organisation as default
    if (isAdmin && !isGod && userClaims?.organisation) {
      setNewUser((prev) => ({ ...prev, organisation: userClaims.organisation }));
    }
  }, [isAdmin, isGod, userClaims]);

  async function handleAddUser(e, confirmation = null) {
    e?.preventDefault();
    if (confirmation && !isGod) {
      setAccountConfirmation(null);
      setAddUserError('Contact BIM Analytics to recover this incomplete account.');
      return;
    }
    const values = confirmation ? confirmation.values : { ...newUser };
    setAddingUser(true);
    setAddUserError('');
    try {
      const body = confirmation
        ? { ...values, confirm_existing_user_id: confirmation.user_id } : values;
      await callSiteFunction('create_user', { method: 'POST', body });
      setAccountConfirmation(null);
      setNewUser(emptyNewUser());
      setShowAddUserForm(false);
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      if (isGod && error.details?.confirmation_required) {
        setAccountConfirmation({ ...error.details.existing_user, values });
      } else if (!isGod && (error.details?.recovery_requires_god
          || error.details?.confirmation_required
          || error.details?.code === 'account_recovery_requires_god')) {
        setAccountConfirmation(null);
        setAddUserError('This account has incomplete setup. Contact BIM Analytics to finish setting it up.');
      } else if (error.details && error.details.account_created) {
        // The Auth account exists but its claims or directory entry do not.
        // The form keeps its values; submitting again resumes from here.
        setAddUserError(
          `The account for ${values.email} exists, but its setup did not finish: ${error.message}. ` +
          (confirmation ? 'Retry the confirmed reset to finish setting it up.' : 'Click Create User again to finish setting it up.')
        );
        loadUsers();
      } else {
        setAddUserError(`Could not create the user: ${error.message}`);
      }
    } finally {
      setAddingUser(false);
    }
  }

  async function runPendingAction(action = pendingAction) {
    const { kind, user } = action;
    setRunningAction(true);
    setActionError('');
    try {
      if (kind === 'delete') {
        await callSiteFunction('delete_user', {
          method: 'DELETE',
          body: { user_id: user.user_id, organisation: user.organisation }
        });
      } else {
        await callSiteFunction('unenroll_user_mfa', {
          method: 'POST',
          body: { user_id: user.user_id, revoke_sessions_only: kind === 'revoke-sessions' }
        });
      }
      setSessionRetries((current) => {
        const remaining = { ...current };
        delete remaining[user.user_id];
        return remaining;
      });
      setPendingAction(null);
    } catch (error) {
      console.error(`Error running ${kind} for ${user.user_id}:`, error);
      setPendingAction(null);
      if (kind !== 'delete' && (error.details?.sessions_revoked
          || error.details?.mfa_removed === null || error.outcomeUnknown)) {
        setSessionRetries((current) => ({ ...current, [user.user_id]: user }));
        setActionError(`${describeUser(user)}: ${error.message}`);
      } else {
        const operation = kind === 'delete' ? 'delete the account'
          : kind === 'revoke-sessions' ? 'sign out sessions' : 'confirm MFA removal';
        setActionError(`Could not ${operation} for ${describeUser(user)}: ${error.message}`);
      }
    } finally {
      loadUsers();
      setRunningAction(false);
    }
  }

  // "Jane Doe (jane@example.com)", falling back to whichever part exists.
  const describeUser = (user) => {
    if (user.name && user.email) return `${user.name} (${user.email})`;
    return user.name || user.email || user.user_id;
  };

  const getRoleClassName = (role) => {
    switch(role) {
      case 'god': return 'tag-role god';
      case 'admin': return 'tag-role admin';
      default: return 'tag-role';
    }
  };

  // The MFA chip has three honest states; a failed lookup is never "No MFA".
  const mfaTag = (user) => {
    if (user.mfa_enrolled === true) {
      return { label: 'MFA', background: '#F0FDF4', color: '#16A34A' };
    }
    if (user.mfa_enrolled === false) {
      return { label: 'No MFA', background: '#FEF3C7', color: '#D97706' };
    }
    return { label: 'MFA unknown', background: '#F3F4F6', color: '#6B7280' };
  };

  // God manages everyone, incomplete accounts included; an admin manages the
  // ordinary members of their own organisation.
  const canManage = (user) =>
    isGod || (isAdmin && user.role === 'user' && user.organisation === userClaims?.organisation);

  const organisationsAvailable = organisationsState.status === 'ready' && organisationsState.organisations.length > 0;

  return (
    <div className="UserManagement">
      <Header />
      <div className="container">
        <h1 className="page-title">User Management</h1>

        {/* Companies - only visible to god users. Companies are provisioned by an
            operator with InsuranceBackend scripts/tennant_mgmt/provision_company.py,
            which creates the Site and Decode halves together; this list is read-only. */}
        {isGod && (
          <>
            <div className="management-card company-card">
              <div className="section-header">
                <h2 className="section-title">
                  <Business style={{ marginRight: '12px', verticalAlign: 'middle' }} />
                  Companies
                </h2>
              </div>
              <p className="section-note">
                New companies are set up by BIM Analytics operations; once provisioned they appear here and in the Add User form.
              </p>
              <div>
                {organisationsState.status === 'loading' && (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <span className="loading-spinner"></span>
                  </div>
                )}
                {organisationsState.status === 'error' && (
                  <div className="error-banner">
                    {organisationsState.error}
                    <button type="button" className="btn-secondary btn-small" onClick={loadOrganisations}>
                      <Refresh style={{ fontSize: '16px' }} />
                      Retry
                    </button>
                  </div>
                )}
                {organisationsState.status === 'ready' && organisationsState.organisations.length === 0 && (
                  <div className="empty-state">
                    <Business className="empty-state-icon" />
                    <div className="empty-state-text">No companies found</div>
                  </div>
                )}
                {organisationsAvailable && (
                  <div className="organisation-grid">
                    {organisationsState.organisations.map((org) => (
                      <div key={org.id} className="organisation-card">
                        <div className="organisation-name">{org.name}</div>
                        <div className="organisation-id">ID: {org.id}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Users Section */}
        <div className="management-card" style={{ marginTop: isGod ? '2rem' : 0 }}>
          <div className="section-header">
            <h2 className="section-title">
              <Group style={{ marginRight: '12px', verticalAlign: 'middle' }} />
              {isGod ? 'All Users' : 'Users'}
            </h2>
          </div>

          {actionError && <div className="error-banner" role="alert">{actionError}</div>}
          {Object.values(sessionRetries).map((user) => (
            <div className="warning-banner" key={user.user_id}>
              Session sign-out could not be confirmed for {describeUser(user)}.
              <button
                type="button"
                className="btn-secondary btn-small"
                disabled={runningAction}
                onClick={() => runPendingAction({ kind: 'revoke-sessions', user })}
              >
                Retry session sign-out
              </button>
            </div>
          ))}

          {directory.mfaLookupError && (
            <div className="warning-banner">
              MFA status could not be checked for some users, so it is shown as unknown. ({directory.mfaLookupError})
              <button type="button" className="btn-secondary btn-small" onClick={() => loadUsers()}>
                <Refresh style={{ fontSize: '16px' }} />
                Check again
              </button>
            </div>
          )}

          {directory.status === 'loading' && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <span className="loading-spinner"></span>
            </div>
          )}

          {directory.status === 'error' && (
            <div className="error-banner">
              {directory.error}
              <button
                type="button"
                className="btn-secondary btn-small"
                onClick={() => loadUsers({ append: Boolean(directory.nextPageToken) })}
              >
                <Refresh style={{ fontSize: '16px' }} />
                Retry
              </button>
            </div>
          )}

          {directory.status === 'ready' && directory.users.length === 0 && (
            <div className="empty-state">
              <Group className="empty-state-icon" />
              <div className="empty-state-text">No users found</div>
            </div>
          )}

          {directory.users.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {directory.users.map((user) => (
                <div key={user.user_id} className="user-card">
                  <div className="user-info">
                    <div className="user-details">
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                      {user.setup_complete ? (
                        <div className="user-meta">
                          <span className="user-tag tag-organisation">
                            <Business style={{ fontSize: '16px', marginRight: '4px' }} />
                            {user.organisation}
                          </span>
                          <span className="user-tag" style={{ background: user.email_verified ? '#F0FDF4' : '#FEF3C7', color: '#1F2937' }}>
                            {user.email_verified ? 'Email verified' : 'Email unverified'}
                          </span>
                          <span className={getRoleClassName(user.role)}>
                            <Shield style={{ fontSize: '16px', marginRight: '4px' }} />
                            {user.role}
                          </span>
                          <span className="user-tag" style={{
                            background: mfaTag(user).background,
                            color: mfaTag(user).color
                          }}>
                            <VpnKey style={{ fontSize: '14px', marginRight: '4px' }} />
                            {mfaTag(user).label}
                          </span>
                        </div>
                      ) : (
                        <div className="user-meta">
                          <span className="user-tag" style={{ background: '#FEE2E2', color: '#B91C1C' }}>
                            Incomplete setup
                          </span>
                          <span className="user-meta-note">
                            Add this user again with the same email to finish setting them up, or delete the account.
                          </span>
                        </div>
                      )}
                    </div>
                    {canManage(user) && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn-secondary btn-small"
                          disabled
                          title="Edit user (coming soon)"
                        >
                          <Edit style={{ fontSize: '16px' }} />
                        </button>
                        {user.mfa_enrolled === true && (
                          <button
                            className="btn-warning btn-small"
                            onClick={() => setPendingAction({ kind: 'remove-mfa', user })}
                            title={`Remove MFA for ${describeUser(user)}`}
                          >
                            <VpnKey style={{ fontSize: '16px' }} />
                          </button>
                        )}
                        <button
                          className="btn-danger btn-small"
                          onClick={() => setPendingAction({ kind: 'delete', user })}
                          title={`Delete ${describeUser(user)}`}
                        >
                          <Delete style={{ fontSize: '16px' }} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {directory.status === 'loading-more' && (
            <div role="status" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              Loading remaining users… {directory.users.length} loaded.
            </div>
          )}
        </div>

        <Dialog open={pendingAction !== null} onClose={() => !runningAction && setPendingAction(null)}>
          {pendingAction && (
            <>
              <DialogTitle>
                {pendingAction.kind === 'delete' ? 'Delete this user?' : 'Remove two-factor authentication?'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>
                  <strong>{pendingAction.user.name || '(no name)'}</strong>
                  <br />
                  {pendingAction.user.email || '(no email)'}
                  {pendingAction.user.organisation && (
                    <>
                      <br />
                      {pendingAction.user.organisation}
                    </>
                  )}
                </DialogContentText>
                <DialogContentText>
                  {pendingAction.kind === 'delete'
                    ? 'Their Site and Decode accounts will be deleted. Existing sessions may retain access for up to one hour. This cannot be undone.'
                    : 'They will be asked to set up two-factor authentication again the next time they log in.'}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPendingAction(null)} disabled={runningAction}>
                  Cancel
                </Button>
                <Button
                  onClick={() => runPendingAction()}
                  color={pendingAction.kind === 'delete' ? 'error' : 'warning'}
                  variant="contained"
                  disabled={runningAction}
                >
                  {pendingAction.kind === 'delete' ? 'Delete user' : 'Remove MFA'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <Dialog open={isGod && Boolean(accountConfirmation)} onClose={() => {
          if (!addingUser) setAccountConfirmation(null);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Reset the existing account?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              An account already exists for {accountConfirmation?.email}.
              Continuing will reset its password to the one entered in this form and assign
              the name {accountConfirmation?.values.name}, organisation {accountConfirmation?.values.organisation},
              and role {accountConfirmation?.values.role}. Its user ID and existing two-factor authentication stay in place.
            </DialogContentText>
            {addUserError && <div className="error-banner" role="alert">{addUserError}</div>}
          </DialogContent>
          <DialogActions>
            <Button disabled={addingUser} onClick={() => setAccountConfirmation(null)}>Cancel</Button>
            <Button color="warning" variant="contained" disabled={addingUser}
              onClick={() => handleAddUser(null, accountConfirmation)}>
              {addingUser ? 'Saving...' : 'Reset account and continue'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add New User Section */}
        {(isGod || isAdmin) && (
          <div className="management-card" style={{ marginTop: '2rem' }}>
            <div className="section-header">
              <h2 className="section-title">
                <PersonAdd style={{ marginRight: '12px', verticalAlign: 'middle' }} />
                Add New User
              </h2>
            </div>

            {!showAddUserForm ? (
              <button
                className="btn-primary"
                onClick={() => setShowAddUserForm(true)}
              >
                <Add style={{ fontSize: '20px' }} />
                Add User
              </button>
            ) : (
              <form onSubmit={handleAddUser} className="form-container">
                <div className="form-group">
                  <label className="form-label" htmlFor="new-user-email">Email</label>
                  <input
                    id="new-user-email"
                    type="email"
                    className="form-input"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="new-user-password">Password</label>
                  <input
                    id="new-user-password"
                    type="password"
                    className="form-input"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    minLength={6}
                    title="At least 6 characters"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="new-user-name">Name</label>
                  <input
                    id="new-user-name"
                    type="text"
                    className="form-input"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="new-user-organisation">Organisation</label>
                  {isAdmin && !isGod ? (
                    <input
                      id="new-user-organisation"
                      className="form-input"
                      readOnly
                      value={organisationsState.organisations.find((org) => org.id === userClaims.organisation)?.name
                        || userClaims.organisation}
                    />
                  ) : (
                    <select
                      id="new-user-organisation"
                      className="form-select"
                      value={newUser.organisation}
                      onChange={(e) => setNewUser({ ...newUser, organisation: e.target.value })}
                      required
                    >
                      <option value="">
                        {organisationsState.status === 'loading' ? 'Loading organisations...' : 'Select an organisation'}
                      </option>
                      {organisationsState.organisations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {organisationsState.status === 'error' && (
                    <div className="error-banner">
                      {organisationsState.error}
                      <button type="button" className="btn-secondary btn-small" onClick={loadOrganisations}>
                        <Refresh style={{ fontSize: '16px' }} />
                        Retry
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="new-user-role">Role</label>
                  <select
                    id="new-user-role"
                    className="form-select"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    required
                  >
                    <option value="user">User</option>
                    {isGod && <option value="admin">Admin</option>}
                  </select>
                </div>

                {addUserError && <div className="error-banner">{addUserError}</div>}

                <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                  <button type="submit" className="btn-primary" disabled={addingUser}>
                    {addingUser ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      <>
                        <PersonAdd style={{ fontSize: '20px' }} />
                        Create User
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowAddUserForm(false);
                      setAddUserError('');
                      setNewUser(emptyNewUser());
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
